<template>
  <dialog-layout>
    <!--Confirm Email After Sign Up-->
    <template v-if="showConfirmEmail">
      <confirm-email :email="email" @confirm="handleEmailConfirmed"></confirm-email>
    </template>

    <template v-else>

      <h1 class="title is-h1">
        Create Your Fit! Account
      </h1>

      <div class="notification is-danger" v-if="submitError">{{ submitError }}</div>

      <form @submit.prevent="handleSubmit">
        <b-field label="Email Address">
          <b-input v-model="email"></b-input>
        </b-field>
        <b-field label="Create Password">
          <b-input type="password" v-model="password"></b-input>
        </b-field>
        <b-field label="Invite Token" v-if="!passedToken">
          <b-input v-model="manualToken"></b-input>
        </b-field>
        <b-field>
          <button type="submit" class="button is-primary" :disabled="isLoading">Create Account</button>
        </b-field>
      </form>

    </template>
  </dialog-layout>
</template>

<script>
import DialogLayout from '../layouts/DialogLayout.vue'
import ConfirmEmail from '../components/auth/ConfirmEmail'
import { validateUserInviteQuery, acceptUserInviteMutation } from '../graphql/users'

export default {
  components: {
    ConfirmEmail,
    DialogLayout
  },
  data () {
    return {
      email: null,
      password: null,
      manualToken: null,
      submitError: null,
      isLoading: false,
      showConfirmEmail: false
    }
  },
  computed: {
    passedToken () {
      return this.$route.params.token
    },
    token () {
      return this.manualToken || this.passedToken
    }
  },
  apollo: {
    invite: {
      query: validateUserInviteQuery,
      fetchPolicy: 'network-only',
      variables () {
        return {
          token: this.token
        }
      },
      update (data) {
        const invite = data.validateUserInvite
        this.email = invite.email
        return invite
      }
    }
  },
  methods: {
    // Start Sign Up
    async handleSubmit () {
      this.isLoading = true
      this.submitError = null

      // destructure data
      const { email, password } = this

      // create a username for this login
      // (or attempt to login if these credentials already exist)
      try {
        await this.createNewAuthUsername(email, password)
      } catch (e) {
        // if there was no username, then stop the flow.
        this.isLoading = false
        this.submitError = e.message
        return
      }

      // login the user.
      // this step will either login the new user or attempt to login the existing user
      try {
        const loggedIn = await this.login()
        // if the login failed, then we'll stop the flow, because the user may be validating their email
        if (!loggedIn) {
          this.isLoading = false
          return
        }
      } catch (e) {
        // display any other errors
        this.isLoading = false
        this.submitError = e.message
        return
      }

      // get the username for the logged in user
      const user = await this.$cognito.cognitoAuthenticatedUser()
      const username = user.getUsername()

      // now that we're logged in, we can accept the invite.
      // we must wait until after we're logged in, so that the correct JWT gets sent to the backend to accept the invite.
      try {
        await this.$apollo.mutate({
          mutation: acceptUserInviteMutation,
          variables: {
            token: this.token,
            email: this.email,
            cognitoUsername: username
          }
        })
      } catch (e) {
        // display any errors
        this.isLoading = false
        this.submitError = e.message
        return
      }

      // re-initialize the auth state, so that the newly created user data is re-emitted and the app can now recognize
      // the authenticated user. during the previous login, the Cognito username was logged in, but it didn't
      // correlate with any actual "User" record.
      await this.$cognito.init()

      // redirect
      this.$router.push({ name: 'home' })
    },

    // After email has been confirmed
    handleEmailConfirmed () {
      // hide the confirm email view
      this.showConfirmEmail = false
      // re-attempt submitting the form
      this.handleSubmit()
    },

    // Create a new Cognito user
    async createNewAuthUsername (email, password) {
      // create the user in Cognito
      try {
        // create the cognito user
        await this.$cognito.signUp(email, password)
      } catch (e) {
        // if there is an error because this user already exists,
        // then we'll let it pass and try to login with it during the next step
        if (e.code === 'UsernameExistsException') {
          return
        }
        // throw any other error
        throw e
      }
    },

    // Login the new username
    async login () {
      try {
        // attempt login
        await this.$cognito.login(this.email, this.password)
        return true
      } catch (e) {
        // if the error is because the email address hasn't been confirmed yet, then show the screen for confirming the code
        if (e.code === 'UserNotConfirmedException') {
          this.showConfirmEmail = true
          return false
        } else {
          // throw all other errors
          throw e
        }
      }
    }
  }
}
</script>
